import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import LogoutButton from "./components/LogoutButton.jsx";
import { goContextualBack } from "./contextualBack.js";

export default function VieService() {
  const navigate = useNavigate();
  const [view, setView] = useState("menu");
  const [demandeType, setDemandeType] = useState("secteur");
  const [demandes, setDemandes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDemande, setSelectedDemande] = useState(null);

  const loadDemandes = async (type) => {
    setLoading(true);
    try {
      const endpoint =
        type === "poste-ph" ? "/api/v2/demandes-poste-ph" : "/api/v2/demandes-changement-secteur";
      const res = await fetch(endpoint, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setDemandes(data);
      }
    } catch (err) {
      console.error("Erreur chargement demandes:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (view === "demandes") loadDemandes(demandeType);
  }, [view, demandeType]);

  const isDemandePositive = (demande) => {
    return demandeType === "poste-ph"
      ? Boolean(demande.demande_poste_ph)
      : Boolean(demande.changement);
  };

  const demandesParMedecin = {};
  demandes.forEach((d) => {
    const key = `${d.prenom} ${d.nom}`;
    if (!demandesParMedecin[key]) demandesParMedecin[key] = [];
    demandesParMedecin[key].push(d);
  });

  const nbPositives = demandes.filter((d) => isDemandePositive(d)).length;
  const nbNegatives = demandes.filter((d) => !isDemandePositive(d)).length;

  if (view === "menu") {
    return (
      <div style={S.container}>
        <div style={S.headerRow}>
          <h1 style={S.title}>Vie du service</h1>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button style={S.backBtn} onClick={() => goContextualBack(navigate)}>
              ← Retour
            </button>
            <LogoutButton />
          </div>
        </div>

        <div style={S.grid}>
          <button
            style={S.menuBtn}
            onClick={() => {
              setDemandeType("secteur");
              setSelectedDemande(null);
              setView("demandes");
            }}
          >
            <div style={S.menuIcon}>🔄</div>
            <div style={S.menuTitle}>Demandes de changement de secteur</div>
            <div style={S.menuDesc}>
              Consulter les demandes de changement de secteur d'activité soumises par les médecins
            </div>
          </button>
          <button
            style={S.menuBtn}
            onClick={() => {
              setDemandeType("poste-ph");
              setSelectedDemande(null);
              setView("demandes");
            }}
          >
            <div style={S.menuIcon}>🏥</div>
            <div style={S.menuTitle}>Poste Praticien Hospitalier - Revision des effectifs 2027</div>
            <div style={S.menuDesc}>
              Consulter les demandes de poste PH transmises par les médecins pour la campagne 2027.
            </div>
          </button>
        </div>
      </div>
    );
  }

  if (view === "demandes") {
    return (
      <div style={S.container}>
        <div style={S.headerRow}>
          <h1 style={S.title}>
            {demandeType === "poste-ph"
              ? "Demandes de poste PH - Revision des effectifs 2027"
              : "Demandes de changement de secteur"}
          </h1>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button style={S.backBtn} onClick={() => selectedDemande ? setSelectedDemande(null) : setView("menu")}>
              ← Retour
            </button>
            <LogoutButton />
          </div>
        </div>

        <div style={S.typeSwitchRow}>
          <button
            type="button"
            style={{
              ...S.typeSwitchBtn,
              ...(demandeType === "secteur" ? S.typeSwitchBtnActive : {}),
            }}
            onClick={() => {
              setDemandeType("secteur");
              setSelectedDemande(null);
            }}
          >
            🔄 Changement de secteur
          </button>
          <button
            type="button"
            style={{
              ...S.typeSwitchBtn,
              ...(demandeType === "poste-ph" ? S.typeSwitchBtnActive : {}),
            }}
            onClick={() => {
              setDemandeType("poste-ph");
              setSelectedDemande(null);
            }}
          >
            🏥 Poste PH 2027
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "40px", color: "#64748b", fontSize: "16px" }}>
            Chargement...
          </div>
        ) : demandes.length === 0 ? (
          <div style={S.emptyState}>
            <div style={{ fontSize: "56px", marginBottom: "16px" }}>📭</div>
            <p style={{ fontSize: "18px", color: "#64748b" }}>Aucune demande pour le moment</p>
            <p style={{ fontSize: "14px", color: "#94a3b8" }}>
              Les médecins peuvent soumettre leurs demandes depuis leur espace personnel.
            </p>
          </div>
        ) : selectedDemande ? (
          /* Détail d'une demande */
          <div style={S.detailCard}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h2 style={{ margin: 0, fontSize: "22px", color: "#1e293b" }}>
                Dr {selectedDemande.prenom} {selectedDemande.nom}
              </h2>
              <span style={{
                ...S.badge,
                background: isDemandePositive(selectedDemande) ? "#fef3c7" : "#dcfce7",
                color: isDemandePositive(selectedDemande) ? "#92400e" : "#166534",
              }}>
                {demandeType === "poste-ph"
                  ? (isDemandePositive(selectedDemande) ? "Poste PH demande" : "Pas de demande")
                  : (isDemandePositive(selectedDemande) ? "Changement demandé" : "Pas de changement")}
              </span>
            </div>

            <div style={S.detailGrid}>
              <div style={S.detailItem}>
                <div style={S.detailLabel}>Date de soumission</div>
                <div style={S.detailValue}>
                  {new Date(selectedDemande.date_soumission).toLocaleDateString("fr-FR", {
                    day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
                  })}
                </div>
              </div>
              <div style={S.detailItem}>
                <div style={S.detailLabel}>Statut</div>
                <div style={S.detailValue}>{selectedDemande.statut || "Non renseigné"}</div>
              </div>
              <div style={S.detailItem}>
                <div style={S.detailLabel}>Temps de travail</div>
                <div style={S.detailValue}>{selectedDemande.temps_travail ? `${selectedDemande.temps_travail}%` : "Non renseigné"}</div>
              </div>
            </div>

            {demandeType === "secteur" && selectedDemande.changement && (
              <>
                <div style={S.sectionSep}>Secteur(s) actuel(s)</div>
                <div style={S.sectorList}>
                  {(selectedDemande.secteurs_actuels || []).map((s, i) => (
                    <span key={i} style={{ ...S.sectorBadge, background: "#fee2e2", color: "#991b1b" }}>{s}</span>
                  ))}
                </div>

                <div style={S.sectionSep}>Secteur(s) souhaité(s)</div>
                <div style={S.sectorList}>
                  {(selectedDemande.secteurs_souhaites || []).map((s, i) => (
                    <span key={i} style={{ ...S.sectorBadge, background: "#dcfce7", color: "#166534" }}>{s}</span>
                  ))}
                </div>

                {selectedDemande.commentaire && (
                  <>
                    <div style={S.sectionSep}>Commentaire</div>
                    <div style={{ background: "#f8fafc", borderRadius: "10px", padding: "14px 16px", fontSize: "15px", color: "#475569", lineHeight: 1.5 }}>
                      {selectedDemande.commentaire}
                    </div>
                  </>
                )}
              </>
            )}

            {demandeType === "poste-ph" && (
              <>
                <div style={S.sectionSep}>Campagne</div>
                <div style={S.detailValue}>{selectedDemande.campagne || "Revision des effectifs 2027"}</div>
                {selectedDemande.commentaire && (
                  <>
                    <div style={S.sectionSep}>Commentaire</div>
                    <div style={{ background: "#f8fafc", borderRadius: "10px", padding: "14px 16px", fontSize: "15px", color: "#475569", lineHeight: 1.5 }}>
                      {selectedDemande.commentaire}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        ) : (
          /* Liste des demandes */
          <>
            {/* Stats */}
            <div style={S.statsRow}>
              <div style={S.statCard}>
                <div style={S.statValue}>{demandes.length}</div>
                <div style={S.statLabel}>Total réponses</div>
              </div>
              <div style={S.statCard}>
                <div style={{ ...S.statValue, color: "#f59e0b" }}>{nbPositives}</div>
                <div style={S.statLabel}>
                  {demandeType === "poste-ph" ? "Demandes poste PH" : "Changements demandés"}
                </div>
              </div>
              <div style={S.statCard}>
                <div style={{ ...S.statValue, color: "#059669" }}>{nbNegatives}</div>
                <div style={S.statLabel}>
                  {demandeType === "poste-ph" ? "Pas de demande" : "Pas de changement"}
                </div>
              </div>
              <div style={S.statCard}>
                <div style={{ ...S.statValue, color: "#2563eb" }}>{Object.keys(demandesParMedecin).length}</div>
                <div style={S.statLabel}>Médecins</div>
              </div>
            </div>

            {/* Liste par médecin */}
            <div style={S.demandeList}>
              {Object.entries(demandesParMedecin)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([medecinKey, medecinDemandes]) => {
                  const derniere = medecinDemandes[medecinDemandes.length - 1];
                  return (
                    <div
                      key={medecinKey}
                      style={S.demandeRow}
                      onClick={() => setSelectedDemande(derniere)}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={S.demandeName}>Dr {medecinKey}</div>
                        <div style={S.demandeDate}>
                          {new Date(derniere.date_soumission).toLocaleDateString("fr-FR", {
                            day: "numeric", month: "long", year: "numeric",
                          })}
                          {derniere.statut && ` · ${derniere.statut}`}
                          {derniere.temps_travail && ` · ${derniere.temps_travail}%`}
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <span style={{
                          ...S.badge,
                          background: isDemandePositive(derniere) ? "#fef3c7" : "#dcfce7",
                          color: isDemandePositive(derniere) ? "#92400e" : "#166534",
                        }}>
                          {demandeType === "poste-ph"
                            ? (isDemandePositive(derniere) ? "Poste PH" : "Pas de demande")
                            : (isDemandePositive(derniere) ? "Changement" : "Pas de changement")}
                        </span>
                        {demandeType === "secteur" && derniere.changement && (
                          <span style={{ fontSize: "13px", color: "#64748b" }}>
                            {(derniere.secteurs_actuels || []).join(", ")} → {(derniere.secteurs_souhaites || []).join(", ")}
                          </span>
                        )}
                        <span style={{ fontSize: "18px", color: "#94a3b8" }}>→</span>
                      </div>
                    </div>
                  );
                })}
            </div>
          </>
        )}
      </div>
    );
  }

  return null;
}

const S = {
  container: {
    maxWidth: "1000px",
    margin: "40px auto",
    padding: "0 24px",
    fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "32px",
  },
  title: { fontSize: "32px", fontWeight: 800, color: "#1e293b", margin: 0 },
  backBtn: {
    padding: "10px 20px",
    borderRadius: "10px",
    backgroundColor: "#2563eb",
    color: "#fff",
    fontSize: "14px",
    fontWeight: 600,
    border: "none",
    cursor: "pointer",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "20px",
  },
  menuBtn: {
    padding: "32px 28px",
    borderRadius: "18px",
    border: "2px solid #e5e7eb",
    background: "white",
    cursor: "pointer",
    textAlign: "left",
    boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
    transition: "transform 0.12s, box-shadow 0.12s",
  },
  menuIcon: { fontSize: "40px", marginBottom: "12px" },
  menuTitle: { fontSize: "20px", fontWeight: 700, color: "#1e293b", marginBottom: "8px" },
  menuDesc: { fontSize: "14px", color: "#64748b", lineHeight: 1.5 },
  emptyState: {
    textAlign: "center",
    padding: "60px 20px",
    background: "white",
    borderRadius: "18px",
    boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
  },
  statsRow: {
    display: "flex",
    gap: "16px",
    marginBottom: "24px",
    flexWrap: "wrap",
  },
  typeSwitchRow: {
    display: "flex",
    gap: "10px",
    marginBottom: "18px",
    flexWrap: "wrap",
  },
  typeSwitchBtn: {
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    borderRadius: "10px",
    padding: "10px 14px",
    fontSize: "14px",
    fontWeight: 700,
    cursor: "pointer",
  },
  typeSwitchBtnActive: {
    border: "1px solid #1d4ed8",
    background: "#dbeafe",
    color: "#1e3a8a",
  },
  statCard: {
    flex: 1,
    minWidth: "130px",
    background: "white",
    borderRadius: "14px",
    padding: "18px 20px",
    textAlign: "center",
    boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
  },
  statValue: { fontSize: "28px", fontWeight: 800, color: "#1e293b" },
  statLabel: { fontSize: "13px", color: "#64748b", marginTop: "4px" },
  demandeList: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  demandeRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "18px 22px",
    background: "white",
    borderRadius: "14px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
    cursor: "pointer",
    border: "1px solid #e5e7eb",
    transition: "background 0.1s",
  },
  demandeName: { fontSize: "16px", fontWeight: 700, color: "#1e293b" },
  demandeDate: { fontSize: "13px", color: "#64748b", marginTop: "2px" },
  badge: {
    padding: "5px 14px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  detailCard: {
    background: "white",
    borderRadius: "18px",
    padding: "32px 28px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
  },
  detailGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "16px",
    marginBottom: "24px",
  },
  detailItem: {
    background: "#f8fafc",
    borderRadius: "12px",
    padding: "14px 16px",
  },
  detailLabel: { fontSize: "12px", fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", marginBottom: "4px" },
  detailValue: { fontSize: "16px", fontWeight: 700, color: "#1e293b" },
  sectionSep: {
    fontSize: "15px",
    fontWeight: 700,
    color: "#475569",
    margin: "20px 0 10px",
    paddingBottom: "6px",
    borderBottom: "2px solid #e5e7eb",
  },
  sectorList: { display: "flex", gap: "10px", flexWrap: "wrap" },
  sectorBadge: {
    padding: "8px 18px",
    borderRadius: "10px",
    fontSize: "15px",
    fontWeight: 700,
  },
};
